
import React, { PropsWithChildren, ReactElement, RefObject, useEffect, useRef, useState } from 'react';
import { MappedComponentProperties } from '@adobe/aem-react-editable-components';

import './ReviewSingleProductModule.css';
import { Button, Section, Title, ReviewsCard, CarouselSlider } from '../../importCL';
import { sanitizeAuthorInput } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import { getBazaarVoiceStarData, IReviewData, StarArray } from '../../foundation/StarArray/StarArray';
import { LinkWrapper } from '../LinkWrapper/LinkWrapper';
import { useWidthContext } from '../../providers/Width';
import { LeftArrow, RightArrow } from '@creditonebank/ui-kit-icons';

type Unpacked<T> = T extends (infer U)[] ? U : T
interface IReviewSingleProductModuleProps extends MappedComponentProperties {
    heading?: string;
    desktopImagePath?: string;
    desktopImageAltText?: string;
    mobileImagePath?: string;
    mobileImageAltText?: string;
    cardImagePath?: string;
    cardImageAltText?: string;
    productName?: string;
    bazaarvoiceCode?: string;
    reviews?: {
        headlineText?: string;
        quoteText?: string;
        authorText?: string;
    }[];
    ctaLink?: string;
    ctaText?: string;
}

export const ReviewSingleProductModuleEditConfig = {
    emptyLabel: 'Review Single Product Module',
    isEmpty: (props: PropsWithChildren<IReviewSingleProductModuleProps>) => {
        return !props || !props.heading;
    },
};

function groupByTwo<T>(arr: T[]): T[][] {
    return  arr.reduce((acc, curr, index) => {
        if (index % 2 === 0) {
            acc.push([curr]);
        } else {
            acc[acc.length - 1].push(curr);
        }
        return acc;
    }, []);
}

const ReviewSingleProductModule = (props: PropsWithChildren<IReviewSingleProductModuleProps>): JSX.Element => {
    const [bvData, setBvData] = useState<IReviewData>({
        average: 0,
        overall: 5,
        total: 0,
    });
    const { width } = useWidthContext();
    const contentRef = useRef();
    const nextArrowRef = useRef();
    const prevArrowRef = useRef();
    const getAverageRating = (avg: number) => (Math.round(avg * 10) / 10).toFixed(1);

    const getSlide = (data: Unpacked<typeof props.reviews>, idx: number) => {
        return (
            <div key={idx} className='reviewSingleProductModule__reviewsCardContainer'>
                <ReviewsCard
                    cardVariant='member-stars'
                    reviewContent={{
                        headLineText: data.headlineText,
                        bodyText: transformHTMLParserLinks(data.quoteText),
                        bottomLineText: data.authorText,
                    }}
                />
            </div>
        );
    };

    const getSlides = () => {
        if (!props.reviews) {
            return [];
        }

        if (width > 1200) {
            return groupByTwo(props.reviews).map((groupedReviews, idx) => {
                const mobileStyles = width < 768 ? 'flex flex-col' : 'flex flex-row';
                return (
                    <div className={`reviewSingleProductModule__slideGroup flex ${mobileStyles} gap-[1.5rem]`} key={idx}>
                        {groupedReviews.map((review, iidx) => getSlide(review, iidx))}
                    </div>
                );
            });
        }
        else {
            const mobileStyles = width < 768 ? 'flex flex-col' : 'flex flex-row';
            return props.reviews.map((slide, index )=> {
                return (
                <div className={`reviewSingleProductModule__slideGroup flex ${mobileStyles} gap-[1.5rem]`} key={index}>
                    {getSlide(slide, index)}
                </div>
            );
            });
            
        }
    };

    const Content = () => {
        return (
            <div ref={contentRef} className='reviewSingleProductModule__content'>
                <Title className={`reviewSingleProductModule--text colTwoRowOne mb-0 ${width >= 1200 ? 'mt-10' : 'mt-6'}`} 
                    tag='h3' styledAs='h2' 
                    align={width >= 1200 ? 'left' : 'center'} 
                    verticalBar>
                    {transformHTMLParserLinks(sanitizeAuthorInput(props.heading, ['br', 'span']))}
                </Title>
                <div className='reviewSingleProductModule__ratingGroup colTwoRowTwo mt-8 mb-8'>
                    <img className='reviewSingleProductModule__cardImage' src={props.cardImagePath} alt={props.cardImageAltText} loading='lazy' decoding='async'/>
                    <div>
                        <Title className='reviewSingleProductModule--text' tag='p' align={width >= 1200 ? 'center' : ''}>
                            {transformHTMLParserLinks(sanitizeAuthorInput(props.productName, ['br']))}
                        </Title>
                        {props.bazaarvoiceCode && (
                            <div className='reviewSingleProductModule__ratingData gap-q'>
                                <div className='reviewSingleProductModule__rating'>
                                    <StarArray size={14}
                                        average={bvData?.average ?? 0} 
                                        total={bvData?.total ?? 0}
                                        overall={bvData?.overall ?? 5} />
                                    <span className='reviewSingleProductModule--text'>{getAverageRating(bvData?.average ?? 0)} / 5</span>
                                </div>
                                {width >= 1200 && <span className='reviewSingleProductModule--text'>|</span>}
                                <span className='reviewSingleProductModule--text'>{(bvData?.total ?? 0).toLocaleString('en-US')} Reviews</span>
                            </div>
                        )}
                    </div>
                </div>
                <Section className='reviewSingleProductModule__sliderSection colTwoRowThree relative' containerClass='!p-0'>
                    <CarouselSlider
                        infinite
                        hideDefaultArrows
                        customArrows={{
                            nextArrowRef: nextArrowRef as RefObject<HTMLElement>,
                            previousArrowRef: prevArrowRef as RefObject<HTMLElement>,
                        }}
                        properties={{
                            slideGap: 16,
                            buttonPadding: 0,
                        }}
                        slides={getSlides()} />
                </Section>
                <div ref={prevArrowRef} className='colOneRowthree'>
                    <LeftArrow className='reviewSingleProductModule__prev' />
                </div>
                <div ref={nextArrowRef} className='colThreeRowthree'>
                    <RightArrow className='reviewSingleProductModule__next' />
                </div>
                {props.ctaLink && props.ctaText && (
                    <LinkWrapper className='reviewSingleProductModule__link colTwoRowFour flex' href={props.ctaLink}>
                        <Button size='medium' variant='link-light' icon='chevron'>{transformHTMLParserLinks(props.ctaText) as unknown as ReactElement}</Button>
                    </LinkWrapper>
                )}
            </div>
        );
    };

    useEffect(() => {
        if (!props.bazaarvoiceCode) {
            return;
        }

        getBazaarVoiceStarData(props.bazaarvoiceCode)
            .then((result) => setBvData(result))
            // eslint-disable-next-line no-console
            .catch((err) => console.debug('Error fetching Bazaarvoice review data', err));
    }, []);

    return (
        <div className='reviewSingleProductModule'>
            <div className='reviewSingleProductModule__background'>
                <div className='reviewSingleProductModule__backgroundImageContainer'>
                    <img
                        src={width >= 1200 ? props.desktopImagePath : props.mobileImagePath}
                        alt={width >= 1200 ? props.desktopImageAltText : props.mobileImageAltText}
                        loading='lazy'
                        decoding='async'
                        fetchPriority='low'
                    />
                </div>
                {width >= 1200 && (
                    <>
                        <div className='reviewSingleProductModule__background--blue' />
                        {Content()}
                    </>
                )}
            </div>
            {width < 1200 && Content()}
        </div>
    );
};

export default ReviewSingleProductModule;
